// #MSDS_LIBRARY_V1 (2026-10-08) — Bill: "There are occasions where the field crews may need an MSDS form ... Currently
// someone from the office has to pull these and often send them everything as they are not sure what will be
// requested. ... Load a button on Safety, then pipe same button on PM Dash to extract PDF files from this depository
// and email whom they wish or perhaps easier, email themselves so they can forward."
//
// One shared component, two doors: Safety → "MSDS / SDS Sheets" nav tile (Safety edit can load / rename / remove),
// PM Dash → "MSDS" header button (pick + send only).
// Files:   public 'safety-files' bucket under msds/  (source folder: Public - Documents\Safety\MSDS Sheets)
// Catalog: app_storage 'safety-msds'          = [{ id, name, lang, file, folder, path, size, type, hash, addedBy, addedAt }]
// Log:     app_storage 'safety-msds-sendlog'  = [{ at, by, to:[...], ids:[...], names:[...] }]  (newest last, capped 500)
// Send:    POST /api/send-email with msdsFiles:[{path,name}] — the SERVER pulls the files from storage and attaches
//          them (Graph's one-request cap ≈ 3 MB of files); anything over the cap goes out as a tap-to-open link.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { makeSafetySigner, openSafetyFile } from "./safetyFiles";   // #SAFETY_PRIVATE_V1

export const MSDS_KEY = 'safety-msds';
const LOG_KEY = 'safety-msds-sendlog';
const BUCKET = 'safety-files';
const MAX_PICK = 15;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OK_EXT = /\.(pdf|docx?)$/i;

const THEMES = {
  safety: { bg:'#0E1113', card:'#171B1F', cardHi:'#1F252B', line:'#2C343C', text:'#F4F6F7', muted:'#95A1A9', accent:'#FDE047', accent2:'#FF6A00', ink:'#0A0C0E', green:'#34D399', red:'#F87171', blue:'#5B8DEF' },
  pm:     { bg:'#0A0E1A', card:'#131A2B', cardHi:'#1A2236', line:'#27304A', text:'#EAF1FF', muted:'#8C9AB8', accent:'#E7B73B', accent2:'#FF7A33', ink:'#0A0E1A', green:'#34D399', red:'#F87171', blue:'#5B8DEF' },
};

// File name → product name. "SDS_Plugz-It_Max-2020-1.pdf" → "Plugz-It Max"; "Krud Kutter (spanish).pdf" → "Krud Kutter" [ES]
export function msdsCleanName(fn) {
  let s = String(fn || '').replace(/^.*[\\/]/, '').replace(/\.[a-z0-9]{2,5}$/i, '');
  const lang = /\(\s*spanish\s*\)|espa[nñ]ol/i.test(s) ? 'ES' : '';
  s = s.replace(/\(\s*(english|spanish)\s*\)/ig, ' ')
    .replace(/material[-_ ]+safety[-_ ]+data[-_ ]+sheet/ig, ' ')
    .replace(/msds\s*document/ig, ' ')
    .replace(/\bSD\s+Sheet\b/ig, ' ')
    .replace(/_\d$/, '')
    .replace(/-SDS-/ig, ' ')
    .replace(/[_+]/g, ' ')
    .replace(/\b(M?SDS|TDS)\b/ig, ' ')
    .replace(/\bsheet\b/ig, ' ')
    .replace(/\brev\s*\d+\b/ig, ' ')
    .replace(/\b\d{1,2}[- ]\d{2}([- ]\d{2})?\b/g, ' ')
    .replace(/\b\d{6,8}\b/g, ' ')
    .replace(/\b20\d\d-\d\b/g, ' ')
    .replace(/^\s*\d{1,2}\s+(?=[A-Za-z])/, '')
    .replace(/\s+-\s*|\s*-\s+/g, ' ')
    .replace(/\s{2,}/g, ' ').trim().replace(/[-\s,]+$/, '').replace(/^[-\s,]+/, '');
  return { name: s || String(fn || 'Sheet'), lang };
}

const extOf = (n) => ((String(n || '').match(/\.([a-z0-9]{2,5})$/i) || [])[1] || 'pdf').toLowerCase();
const ctypeOf = (ext) => ext === 'pdf' ? 'application/pdf'
  : ext === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  : ext === 'doc' ? 'application/msword' : 'application/octet-stream';
const kb = (b) => !b ? '' : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;' }[c]));
const sendName = (x) => `${x.name}${x.lang === 'ES' ? ' (Spanish)' : ''}.${x.type || 'pdf'}`.replace(/[\\/:*?"<>|#%]/g, '-');
const personOf = (upn) => String(upn || '').split('@')[0].split(/[._]/).filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1)).join(' ');

async function sha(file) {
  try {
    const h = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(h)).map(x => x.toString(16).padStart(2, '0')).join('').slice(0, 40);
  } catch { return ''; }
}
// Dropped folders: walk the tree so a whole "MSDS Sheets" folder can be dragged in at once.
function walkEntry(entry, prefix) {
  return new Promise((resolve) => {
    if (!entry) return resolve([]);
    if (entry.isFile) return entry.file(f => resolve([{ f, rel: prefix + f.name }]), () => resolve([]));
    if (!entry.isDirectory) return resolve([]);
    const reader = entry.createReader(); const all = [];
    const more = () => reader.readEntries(async (ents) => {
      if (!ents.length) { const nested = await Promise.all(all.map(e => walkEntry(e, prefix + entry.name + '/'))); resolve(nested.flat()); }
      else { all.push(...ents); more(); }
    }, () => resolve([]));
    more();
  });
}

export default function MsdsLibrary({ sg, ss, supaUrl, supaKey, getToken, userUpn, editable, theme = 'safety', asModal, onClose }) {
  const P = THEMES[theme] || THEMES.safety;
  const me = String(userUpn || '').trim().toLowerCase();
  const [list, setList] = useState(null);
  const [log, setLog] = useState([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [toMe, setToMe] = useState(true);
  const [others, setOthers] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);          // { ok, text }
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [delId, setDelId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const filesRef = useRef(null), folderRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => { listRef.current = list; }, [list]);

  useEffect(() => { (async () => {
    try { const v = await sg(MSDS_KEY); setList(Array.isArray(v) ? v : []); } catch { setList([]); }
    try { const l = await sg(LOG_KEY); setLog(Array.isArray(l) ? l : []); } catch {}
  })(); }, [sg]);

  // #SAFETY_PRIVATE_V1 — bucket is private; View signs the link with the viewer's sign-in, then opens it
  const signSafety = makeSafetySigner(supaUrl, supaKey, getToken);
  const hdr = async (extra) => { const tok = getToken ? await getToken() : null; return { apikey: supaKey, Authorization: `Bearer ${tok || supaKey}`, ...(extra || {}) }; };
  const saveList = async (next) => { listRef.current = next; setList(next); try { await ss(MSDS_KEY, next); } catch (e) { setMsg({ ok:false, text:'Could not save the list: ' + (e.message || e) }); } };

  const sorted = useMemo(() => (list || []).slice().sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric:true, sensitivity:'base' }) || String(a.lang).localeCompare(String(b.lang))), [list]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase(); if (!t) return sorted;
    const words = t.split(/\s+/);
    return sorted.filter(x => { const hay = `${x.name} ${x.file} ${x.folder || ''} ${x.lang === 'ES' ? 'spanish espanol' : ''}`.toLowerCase(); return words.every(w => hay.includes(w)); });
  }, [sorted, q]);
  // Most pulled (last 12 months) — one tap to pick the usual ones.
  const popular = useMemo(() => {
    const cut = Date.now() - 365 * 864e5; const n = {};
    (log || []).forEach(r => { if (Date.parse(r.at) >= cut) (r.ids || []).forEach(id => { n[id] = (n[id] || 0) + 1; }); });
    const byId = new Map((list || []).map(x => [x.id, x]));
    return Object.entries(n).filter(([id]) => byId.has(id)).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([id, c]) => ({ ...byId.get(id), _n:c }));
  }, [log, list]);
  const picks = useMemo(() => sorted.filter(x => sel.has(x.id)), [sorted, sel]);
  const pickBytes = picks.reduce((s, x) => s + (x.size || 0), 0);

  const toggle = (id) => {
    if (!sel.has(id) && sel.size >= MAX_PICK) { setMsg({ ok:false, text:`Up to ${MAX_PICK} sheets per e-mail — send these, then pick more.` }); return; }
    setSel(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  // ── Load (Safety edit) ───────────────────────────────────────────────────────
  const loadItems = async (items) => {
    if (!editable) return;
    const tok = getToken ? await getToken() : null;
    if (!tok) { setMsg({ ok:false, text:'Your sign-in has expired — reload the page (Ctrl+Shift+R), then load again.' }); return; }
    const good = items.filter(it => OK_EXT.test(it.f.name));
    const skippedType = items.length - good.length;
    if (!good.length) { setMsg({ ok:false, text:'No PDF or Word files in that selection (zip files are skipped — unzip them first).' }); return; }
    setMsg(null);
    // #MSDS_FRESH_V1 — start from the saved list, not what this tab opened with, so a cleanup made
    // elsewhere (another user, or Claude via SQL) isn't written back over.
    let cur = (listRef.current || []).slice();
    try { const fresh = await sg(MSDS_KEY); if (Array.isArray(fresh)) cur = fresh.slice(); } catch {}
    const seen = new Set(cur.map(x => x.hash).filter(Boolean));
    let added = 0, dup = 0; const failed = [];
    for (let i = 0; i < good.length; i++) {
      const { f, rel } = good[i];
      setBusy(`Loading ${i + 1} of ${good.length} — ${f.name}`);
      const h = await sha(f);
      if (h && seen.has(h)) { dup++; continue; }
      const ext = extOf(f.name);
      const path = `msds/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
      try {
        const res = await fetch(`${supaUrl}/storage/v1/object/${BUCKET}/${path}`, { method:'POST', headers: await hdr({ 'Content-Type': f.type || ctypeOf(ext), 'x-upsert':'true' }), body: f });
        if (!res.ok) {
          const t = (await res.text().catch(() => '')).slice(0, 160);
          failed.push(`${f.name} (${res.status}${/security|denied|unauthor/i.test(t) ? ' — needs Safety edit / fresh sign-in' : ''})`);
          if (failed.length >= 3 && added === 0) break;   // it's the sign-in, not the files — stop hammering
          continue;
        }
      } catch (e) { failed.push(`${f.name} (${e.message || e})`); continue; }
      const parts = String(rel || '').split('/').filter(Boolean);
      const folder = parts.length > 2 ? parts.slice(1, -1).join(' / ') : '';   // first part = the folder that was picked
      const { name, lang } = msdsCleanName(f.name);
      cur.push({ id:`msds-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name, lang, file:f.name, folder, path, size:f.size, type:ext, hash:h, addedBy:me, addedAt:new Date().toISOString() });
      if (h) seen.add(h);
      added++;
      if (added % 10 === 0) await saveList(cur.slice());   // a closed tab mid-load keeps what landed
    }
    await saveList(cur);
    setBusy('');
    setMsg({ ok: !failed.length, text: `Added ${added} sheet${added === 1 ? '' : 's'}`
      + (dup ? ` · ${dup} duplicate${dup === 1 ? '' : 's'} skipped` : '')
      + (skippedType ? ` · ${skippedType} non-PDF/Word skipped` : '')
      + (failed.length ? ` · ${failed.length} failed: ${failed.slice(0, 4).join('; ')}${failed.length > 4 ? '…' : ''}` : '') });
  };
  const onPickFiles = (e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; loadItems(fs.map(f => ({ f, rel: f.webkitRelativePath || '' }))); };
  const onDrop = async (e) => {
    e.preventDefault(); setDragOver(false); if (!editable) return;
    const its = Array.from(e.dataTransfer.items || []);
    const entries = its.map(it => it.webkitGetAsEntry ? it.webkitGetAsEntry() : null).filter(Boolean);
    if (entries.length) { const got = (await Promise.all(entries.map(en => walkEntry(en, '')))).flat(); loadItems(got.map(g => ({ f:g.f, rel:g.rel }))); }
    else loadItems(Array.from(e.dataTransfer.files || []).map(f => ({ f, rel:'' })));
  };
  const freshList = async () => { try { const v = await sg(MSDS_KEY); if (Array.isArray(v)) return v; } catch {} return listRef.current || []; };   // #MSDS_FRESH_V1
  const rename = async (id) => {
    const nm = editName.trim(); setEditId(null); if (!nm) return;
    await saveList((await freshList()).map(x => x.id === id ? { ...x, name:nm } : x));
  };
  const toggleLang = async (id) => saveList((await freshList()).map(x => x.id === id ? { ...x, lang: x.lang === 'ES' ? '' : 'ES' } : x));
  const remove = async (x) => {
    setDelId(null);
    await saveList((await freshList()).filter(y => y.id !== x.id));
    setSel(prev => { const n = new Set(prev); n.delete(x.id); return n; });
    try { await fetch(`${supaUrl}/storage/v1/object/${BUCKET}/${x.path}`, { method:'DELETE', headers: await hdr() }); } catch {}
  };

  // ── Send ─────────────────────────────────────────────────────────────────────
  const extraList = others.split(/[;,\s]+/).map(s => s.trim()).filter(Boolean);
  const badExtra = extraList.filter(s => !EMAIL_RE.test(s));
  const recipients = Array.from(new Set([...(toMe && EMAIL_RE.test(me) ? [me] : []), ...extraList.filter(s => EMAIL_RE.test(s)).map(s => s.toLowerCase())]));
  const send = async () => {
    if (!picks.length) return;
    if (badExtra.length) { setMsg({ ok:false, text:`Check the address: ${badExtra.join(', ')}` }); return; }
    if (!recipients.length) { setMsg({ ok:false, text:'Tick "Send to me" or type at least one e-mail address.' }); return; }
    setBusy(`Sending ${picks.length} sheet${picks.length === 1 ? '' : 's'}…`); setMsg(null);
    const who = personOf(me) || me || 'DOPE';
    const names = picks.map(x => x.name + (x.lang === 'ES' ? ' (Spanish)' : ''));
    const subject = `SDS / MSDS — ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` +${names.length - 3} more` : ''}`;
    const html = `<div style="font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#222">`
      + `<p><b>${esc(who)}</b> pulled ${picks.length} Safety Data Sheet${picks.length === 1 ? '' : 's'} from DOPE.${note.trim() ? `<br/>Note: ${esc(note.trim())}` : ''}</p>`
      + `<p style="color:#555">Forward this e-mail to whoever needs it. Sheets are attached where they fit; every sheet also has a link below that opens on any phone.</p></div>`;
    try {
      const r = await fetch('/api/send-email', { method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ to: recipients, subject, html, msdsFiles: picks.map(x => ({ path:x.path, name: sendName(x) })) }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.message || j.error || `HTTP ${r.status}`);
      const m = j.msds || {};
      const att = (m.attached || []).length, lnk = (m.linked || []).length;
      setMsg({ ok:true, text: `Sent to ${recipients.join(', ')} — ${att} attached${lnk ? `, ${lnk} as link${lnk === 1 ? '' : 's'} (too big to attach)` : ''}.` });
      setSel(new Set()); setNote('');
      try {
        const cur = await sg(LOG_KEY);
        const next = [...(Array.isArray(cur) ? cur : []), { at:new Date().toISOString(), by:me, to:recipients, ids:picks.map(x => x.id), names }].slice(-500);
        await ss(LOG_KEY, next); setLog(next);
      } catch {}
    } catch (e) {
      setMsg({ ok:false, text: 'Send failed: ' + (e.message || e) + ' — you can still tap View on a sheet and share it from your phone.' });
    }
    setBusy('');
  };

  // ── UI ───────────────────────────────────────────────────────────────────────
  const inp = { background:P.card, border:`1px solid ${P.line}`, color:P.text, borderRadius:8, padding:'9px 11px', fontSize:14, outline:'none', boxSizing:'border-box', width:'100%' };
  const btn = (c, solid) => ({ background: solid ? c : 'transparent', color: solid ? P.ink : c, border:`1px solid ${c}`, borderRadius:8, padding:'8px 13px', fontSize:12.5, fontWeight:800, cursor:'pointer', whiteSpace:'nowrap' });
  const tag = (c) => ({ fontSize:9.5, fontWeight:900, letterSpacing:.4, color:c, border:`1px solid ${c}88`, borderRadius:5, padding:'1px 5px', textTransform:'uppercase' });

  const body = (
    <div onDragOver={e => { if (editable) { e.preventDefault(); setDragOver(true); } }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
      style={{ color:P.text, outline: dragOver ? `2px dashed ${P.accent}` : 'none', outlineOffset:4, borderRadius:12 }}>
      <div style={{ fontSize:12.5, color:P.muted, marginBottom:12, lineHeight:1.5 }}>
        Find the product, tick it, and e-mail it to yourself (or anyone) — forward it on from your phone. <b style={{ color:P.text }}>View</b> opens the sheet right here.
      </div>

      {editable && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', background:P.cardHi, border:`1px solid ${P.line}`, borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
          <span style={{ fontSize:11, fontWeight:900, letterSpacing:.6, textTransform:'uppercase', color:P.accent }}>Safety · manage</span>
          <button disabled={!!busy} onClick={() => folderRef.current && folderRef.current.click()} style={btn(P.accent, true)}>Load a folder…</button>
          <button disabled={!!busy} onClick={() => filesRef.current && filesRef.current.click()} style={btn(P.accent)}>+ Add sheets</button>
          <span style={{ fontSize:11.5, color:P.muted }}>or drag the MSDS Sheets folder / PDFs onto this box · duplicates are skipped</span>
          <input ref={filesRef} type="file" multiple accept=".pdf,.doc,.docx" onChange={onPickFiles} style={{ display:'none' }} />
          <input ref={el => { folderRef.current = el; if (el) { el.setAttribute('webkitdirectory', ''); el.setAttribute('directory', ''); } }} type="file" multiple onChange={onPickFiles} style={{ display:'none' }} />
        </div>
      )}

      {busy && <div style={{ fontSize:12.5, color:P.accent, fontWeight:800, marginBottom:10 }}>{busy}</div>}
      {msg && <div onClick={() => setMsg(null)} title="Click to dismiss" style={{ fontSize:12.5, fontWeight:700, marginBottom:10, padding:'8px 11px', borderRadius:8, cursor:'pointer', color: msg.ok ? P.green : P.red, background: `${msg.ok ? P.green : P.red}14`, border:`1px solid ${msg.ok ? P.green : P.red}55` }}>{msg.text}</div>}

      <div style={{ position:'relative', marginBottom:10 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={`🔎 Search ${list ? list.length + ' ' : ''}sheets — product, brand, "spanish"…`} style={inp} autoFocus={!!asModal} />
        {q && <span onClick={() => setQ('')} style={{ position:'absolute', right:10, top:8, cursor:'pointer', color:P.muted, fontSize:16 }}>✕</span>}
      </div>

      {!q && popular.length > 0 && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center', marginBottom:10 }}>
          <span style={{ fontSize:10.5, fontWeight:900, letterSpacing:.6, textTransform:'uppercase', color:P.muted }}>Most pulled</span>
          {popular.map(x => (
            <button key={x.id} onClick={() => toggle(x.id)} style={{ ...btn(sel.has(x.id) ? P.green : P.blue, sel.has(x.id)), padding:'4px 10px', fontSize:11.5 }}>
              {sel.has(x.id) ? '✓ ' : ''}{x.name}{x.lang === 'ES' ? ' (ES)' : ''}
            </button>
          ))}
        </div>
      )}

      {list == null ? <div style={{ color:P.muted, padding:20 }}>Loading sheets…</div>
        : !list.length ? (
          <div style={{ padding:'26px 16px', textAlign:'center', color:P.muted, border:`1px dashed ${P.line}`, borderRadius:12 }}>
            {editable ? <>No sheets loaded yet. Click <b style={{ color:P.accent }}>Load a folder…</b> and pick <b style={{ color:P.text }}>Public - Documents › Safety › MSDS Sheets</b> (or drag that folder here).</>
              : <>No sheets loaded yet — Safety is loading the MSDS library.</>}
          </div>
        ) : (
          <div style={{ border:`1px solid ${P.line}`, borderRadius:12, overflow:'hidden' }}>
            <div style={{ fontSize:11, color:P.muted, padding:'6px 12px', background:P.cardHi, borderBottom:`1px solid ${P.line}` }}>
              {shown.length} of {list.length} sheet{list.length === 1 ? '' : 's'}{q ? ` matching "${q}"` : ''}
            </div>
            <div style={{ maxHeight: asModal ? 'calc(92vh - 380px)' : 520, minHeight:140, overflowY:'auto' }}>
              {shown.map(x => {
                const on = sel.has(x.id);
                return (
                  <div key={x.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', borderBottom:`1px solid ${P.line}`, background: on ? `${P.green}12` : 'transparent' }}>
                    <span onClick={() => toggle(x.id)} role="checkbox" aria-checked={on}
                      style={{ flex:'0 0 auto', width:22, height:22, borderRadius:6, border:`2px solid ${on ? P.green : P.muted}`, background: on ? P.green : 'transparent', color:P.ink, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:14, cursor:'pointer' }}>{on ? '✓' : ''}</span>
                    <div style={{ flex:1, minWidth:0 }} onClick={() => { if (editId !== x.id) toggle(x.id); }}>
                      {editId === x.id ? (
                        <input autoFocus value={editName} onChange={e => setEditName(e.target.value)} onBlur={() => rename(x.id)}
                          onKeyDown={e => { if (e.key === 'Enter') rename(x.id); if (e.key === 'Escape') setEditId(null); }} onClick={e => e.stopPropagation()} style={{ ...inp, padding:'5px 8px', fontSize:13 }} />
                      ) : (
                        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', cursor:'pointer' }}>
                          <span style={{ fontWeight:800, fontSize:13.5 }}>{x.name}</span>
                          {x.lang === 'ES' && <span style={tag(P.accent2)}>Español</span>}
                          {x.type && x.type !== 'pdf' && <span style={tag(P.blue)}>{x.type}</span>}
                          {x.folder && <span style={tag(P.muted)}>{x.folder}</span>}
                        </div>
                      )}
                      <div style={{ fontSize:10.5, color:P.muted, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{x.file} · {kb(x.size)}</div>
                    </div>
                    <a href="#view" onClick={e => { e.preventDefault(); e.stopPropagation(); if (x && x.path) openSafetyFile(signSafety, x.path); }} style={{ ...btn(P.blue), padding:'5px 10px', fontSize:11.5, textDecoration:'none' }}>View</a>
                    {editable && editId !== x.id && (
                      delId === x.id
                        ? <span style={{ display:'flex', gap:4 }}>
                            <button onClick={() => remove(x)} style={{ ...btn(P.red, true), padding:'5px 8px', fontSize:11 }}>Remove</button>
                            <button onClick={() => setDelId(null)} style={{ ...btn(P.muted), padding:'5px 8px', fontSize:11 }}>Keep</button>
                          </span>
                        : <span style={{ display:'flex', gap:2 }}>
                            <span title="Rename" onClick={() => { setEditId(x.id); setEditName(x.name); }} style={{ cursor:'pointer', color:P.muted, padding:'4px 5px', fontSize:13 }}>✎</span>
                            <span title="Toggle Spanish tag" onClick={() => toggleLang(x.id)} style={{ cursor:'pointer', color: x.lang === 'ES' ? P.accent2 : P.muted, padding:'4px 5px', fontSize:11, fontWeight:900 }}>ES</span>
                            <span title="Remove from library" onClick={() => setDelId(x.id)} style={{ cursor:'pointer', color:P.muted, padding:'4px 5px', fontSize:13 }}>🗑</span>
                          </span>
                    )}
                  </div>
                );
              })}
              {!shown.length && <div style={{ padding:18, color:P.muted, fontSize:13 }}>Nothing matches "{q}". Try one word of the product name.</div>}
            </div>
          </div>
        )}

      {/* send bar */}
      <div style={{ position:'sticky', bottom:0, marginTop:12, background:P.cardHi, border:`1px solid ${picks.length ? P.green : P.line}`, borderRadius:12, padding:'11px 12px', boxShadow: picks.length ? `0 0 22px -8px ${P.green}` : 'none' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom: picks.length ? 8 : 0 }}>
          <span style={{ fontWeight:900, fontSize:13.5, color: picks.length ? P.green : P.muted }}>{picks.length ? `${picks.length} selected` : 'Tick the sheets you need'}</span>
          {picks.length > 0 && <span style={{ fontSize:11, color:P.muted }}>{kb(pickBytes)}{pickBytes > 2.7 * 1048576 ? ' · the biggest ones go as links' : ''}</span>}
          {picks.length > 0 && <span onClick={() => setSel(new Set())} style={{ fontSize:11.5, color:P.muted, cursor:'pointer', textDecoration:'underline', marginLeft:'auto' }}>clear</span>}
        </div>
        {picks.length > 0 && (
          <>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
              {picks.map(x => <span key={x.id} onClick={() => toggle(x.id)} title="Remove" style={{ fontSize:11.5, background:P.card, border:`1px solid ${P.line}`, borderRadius:20, padding:'3px 9px', cursor:'pointer' }}>{x.name}{x.lang === 'ES' ? ' (ES)' : ''} ✕</span>)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:8, alignItems:'center' }}>
              <label style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, cursor:'pointer', minWidth:0 }}>
                <input type="checkbox" checked={toMe} onChange={e => setToMe(e.target.checked)} style={{ width:18, height:18, flex:'0 0 auto' }} />
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>Send to me <span style={{ color:P.muted, fontSize:11.5 }}>({me || 'no e-mail on file'})</span></span>
              </label>
              <input value={others} onChange={e => setOthers(e.target.value)} placeholder="Also send to (e-mail, comma separated)" style={{ ...inp, fontSize:13, borderColor: badExtra.length ? P.red : P.line }} />
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional) — job #, who asked" style={{ ...inp, fontSize:13 }} />
              <button disabled={!!busy || !recipients.length} onClick={send} style={{ ...btn(P.green, true), padding:'10px 14px', fontSize:13.5, opacity: (busy || !recipients.length) ? .5 : 1 }}>
                ✉ E-mail {picks.length} sheet{picks.length === 1 ? '' : 's'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (!asModal) return body;
  return createPortal(
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.62)', zIndex:9999, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'4vh 10px', boxSizing:'border-box', overflowY:'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'min(780px, 100%)', background:P.bg, border:`1px solid ${P.line}`, borderRadius:16, padding:'16px 16px 14px', boxShadow:`0 20px 60px rgba(0,0,0,.6), 0 0 0 1px ${P.accent}22`, boxSizing:'border-box' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <div style={{ fontSize:11, fontWeight:900, letterSpacing:2, color:P.accent }}>SAFETY</div>
          <div style={{ fontSize:19, fontWeight:900, color:P.text }}>MSDS / SDS Sheets</div>
          <button onClick={onClose} style={{ marginLeft:'auto', background:'transparent', border:`1px solid ${P.line}`, color:P.text, borderRadius:8, padding:'5px 11px', fontSize:13, fontWeight:800, cursor:'pointer' }}>Close ✕</button>
        </div>
        {body}
      </div>
    </div>, document.body);
}
